/**
 * Auto-Match Confirmation Dialog
 * Shows communication methods for all matched providers with Phase 2 ranking
 */

import { useState } from 'react';
import { X, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { CommunicationMethodSelector } from './CommunicationMethodSelector';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

// Helper function to parse services which may be a string or array
const parseServices = (services: string | string[] | undefined | null): string[] => {
  if (!services) return [];
  if (Array.isArray(services)) return services;
  if (typeof services === 'string') {
    try {
      const parsed = JSON.parse(services);
      if (Array.isArray(parsed)) return parsed;
      return [services];
    } catch {
      if (services.includes(',')) {
        return services.split(',').map(s => s.trim()).filter(Boolean);
      }
      return [services];
    }
  }
  return [];
};

interface MatchedProvider {
  provider_id: string;
  provider_name: string;
  organization_name: string;
  distance_miles: number;
  services: string[];
  status: 'pending' | 'sent' | 'accepted' | 'not_selected';
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
}

interface AutoMatchConfirmationDialogProps {
  referral: {
    id: string;
    first_name: string;
    last_name: string;
    sdoh_needs: string[];
    zip_code: string;
  };
  matchedProviders: MatchedProvider[];
  onConfirm: (selectedMethods: Map<string, any>) => Promise<void>;
  onCancel: () => void;
}

export function AutoMatchConfirmationDialog({
  referral,
  matchedProviders,
  onConfirm,
  onCancel
}: AutoMatchConfirmationDialogProps) {
  const [expandedProviderId, setExpandedProviderId] = useState<string | null>(matchedProviders[0]?.provider_id || null);
  const [selectedMethods, setSelectedMethods] = useState<Map<string, any>>(new Map());
  const [sending, setSending] = useState(false);

  const handleMethodSelected = (providerId: string, method: any) => {
    setSelectedMethods(prev => {
      const updated = new Map(prev);
      updated.set(providerId, method);
      return updated;
    });
  };

  const handleConfirm = async () => {
    // Check if all providers have a method selected
    const missingMethods = matchedProviders.filter(
      p => !selectedMethods.has(p.provider_id)
    );

    if (missingMethods.length > 0) {
      alert(`Please select a communication method for all ${matchedProviders.length} providers before sending.`);
      return;
    }

    setSending(true);
    try {
      await onConfirm(selectedMethods);
    } finally {
      setSending(false);
    }
  };

  const toggleProvider = (providerId: string) => {
    setExpandedProviderId(expandedProviderId === providerId ? null : providerId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-2 sm:p-4">
      <div className="w-full max-w-4xl my-4 sm:my-8 min-h-0 flex">
        <Card className="w-full bg-white shadow-2xl flex flex-col max-h-[95vh]">
          <CardContent className="p-3 sm:p-6 bg-white flex flex-col min-h-0 max-h-[95vh]">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between mb-3 sm:mb-4 flex-shrink-0">
              <div className="flex-1 min-w-0 mr-2">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                  Send Referral to {matchedProviders.length} Provider{matchedProviders.length !== 1 ? 's' : ''}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
                  Member: {referral.first_name} {referral.last_name} | Needs: {referral.sdoh_needs.join(', ')}
                </p>
              </div>
              <button
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                aria-label="Cancel"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Scrollable Provider List */}
            <div className="space-y-3 sm:space-y-4 overflow-y-auto flex-1 min-h-0">
            {matchedProviders.map((provider, index) => {
              const isExpanded = expandedProviderId === provider.provider_id;
              const hasMethodSelected = selectedMethods.has(provider.provider_id);

              return (
                <Card
                  key={provider.provider_id}
                  className={`border-2 ${hasMethodSelected ? 'border-green-500' : 'border-gray-200'}`}
                >
                  <CardContent className="p-2 sm:p-4">
                    {/* Provider Header */}
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleProvider(provider.provider_id)}
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-xs sm:text-base">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{provider.provider_name}</h3>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">
                            {provider.distance_miles.toFixed(1)} mi
                            {(() => {
                              const servicesList = parseServices(provider.services);
                              return servicesList.length > 0 ? ` • ${servicesList.slice(0, 1).join(', ')}` : '';
                            })()}
                          </p>
                        </div>
                        {hasMethodSelected && (
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                        )}
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    {/* Communication Method Selection - Expanded */}
                    {isExpanded && (
                      <div className="mt-2 sm:mt-4 pt-2 sm:pt-4 border-t">
                        <CommunicationMethodSelector
                          providerId={provider.provider_id}
                          providerName={provider.provider_name}
                          referralId={referral.id}
                          fallbackContacts={{
                            phone: provider.phone,
                            email: provider.email,
                            website: provider.website,
                            address: provider.address
                          }}
                          onMethodSelected={(method) => handleMethodSelected(provider.provider_id, method)}
                          onSend={async (method, message) => {
                            // For auto-match, we don't send immediately
                            // Just track the selection
                            handleMethodSelected(provider.provider_id, method);
                            alert(`Method selected for ${provider.provider_name}. Click "Send to All Providers" to send all referrals.`);
                          }}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Action Buttons - Sticky Footer */}
          <div className="flex items-center justify-between pt-3 sm:pt-6 border-t flex-shrink-0 bg-white mt-3 sm:mt-4">
            <p className="text-xs sm:text-sm text-gray-600">
              {selectedMethods.size} of {matchedProviders.length} providers ready
            </p>
            <div className="flex space-x-2 sm:space-x-3">
              <Button
                variant="outline"
                onClick={onCancel}
                disabled={sending}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={sending || selectedMethods.size < matchedProviders.length}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white"
                size="sm"
              >
                {sending ? (
                  <>Sending to {matchedProviders.length} Provider{matchedProviders.length !== 1 ? 's' : ''}...</>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Send to All {matchedProviders.length} Providers</span>
                    <span className="sm:hidden">Send to {matchedProviders.length}</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

export default AutoMatchConfirmationDialog;
